// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { CopyConnectionItem } from './copy-connection-item';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUtils } from '@article/util/article-utils';
import { ModelLoadingService } from '@article/service/model-loading.service';
import { ArticleModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { CopyArticlePropertiesFromOperation } from '../../../operation/copy-article-properties-from-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { CopyFromOperationState } from './copy-from-operation-state';
import { PropertyUtils } from '@article/util/property-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ConnectionConverter } from '@article/components/connections/connection-creation/connection-converter';
import { AdminService } from '../../../services/admin.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { AdmintoolOperationFactory } from '../../../operation/admintool-operation.factory';
import { CloneUtils } from '@shared/util/clone-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { Euler, Mesh, Vector3 } from 'three';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { ConnectionFactory } from '@3d-scene/model3d/connection.factory';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

@Component({
  standalone: false,
  selector: 'app-copy-properties-modal',
  templateUrl: './copy-properties-modal.component.html',
  styleUrls: ['./copy-properties-modal.component.scss'],
})
export class CopyPropertiesModalComponent implements OnInit {

  @Input()
  targetArticleVO: ArticleVO;
  @Input()
  sourceArticleVO: ArticleVO;

  public sourceConnectionList: CopyConnectionItem[] = [];
  public targetConnectionList: CopyConnectionItem[] = [];
  public unusedSourceConnections: MetaElementVO[];
  public copyConnections: boolean = true;
  public isCopyConnectionsDisabled: boolean = false;
  public isPropertiesChecked: boolean = false;
  public isAttributesChecked: boolean = false;
  public copyConnectionProperties: boolean = true;
  public copyBehaviorProperties: boolean = true;
  public copyPlausiProperties: boolean = true;

  private targetContainer: ArticleContainer;

  constructor(
    private bsModalRef: BsModalRef,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private modelLoadingService: ModelLoadingService,
    private undoRedoHelper: UndoRedoHelper,
    private selectionModelService: SelectionModelService,
    private sceneManager: SceneManager,
    private articleContainerFactory: ArticleContainerFactory,
    private connectionUtils: ConnectionUtils,
    private containerService: ContainerService,
    private connectionConverter: ConnectionConverter,
    private adminService: AdminService,
    private applicationRefreshService: ApplicationRefreshService,
    private admintoolOperationFactory: AdmintoolOperationFactory,
    private articleManager: ArticleManager,
    private connectionFactory: ConnectionFactory
  ) {}

  ngOnInit(): void {
    this.sourceConnectionList = this.generateSourceConnectionList(this.sourceArticleVO);
    if (
      ArticleUtils.isCompositeArticle(this.targetArticleVO) ||
      ArticleUtils.isCompositeArticle(this.sourceArticleVO) ||
      ArrayUtils.isEmpty(this.sourceConnectionList)
    ) {
      this.copyConnections = false;
      this.isCopyConnectionsDisabled = true;
    }
    this.targetConnectionList = this.generateTargetConnectionList(this.targetArticleVO, this.sourceConnectionList);
    this.addNewConnectionItemsToTargetFromSource();
  }

  private generateSourceConnectionList(articleVO: ArticleVO): CopyConnectionItem[] {
    let result: CopyConnectionItem[] = [];
    if (articleVO && articleVO.metaElements) {
      result = MetaElementUtils.excludeInternalMetaElements(articleVO.metaElements).map((connection: MetaElementVO) => {
        const connectionName = this.getMainConnectionName(connection.name);
        return new CopyConnectionItem(connectionName);
      });
    }
    return result;
  }

  private generateTargetConnectionList(articleVO: ArticleVO, sourceConnections: CopyConnectionItem[]): CopyConnectionItem[] {
    let result: CopyConnectionItem[] = [];
    if (articleVO && articleVO.metaElements) {
      const metaElements: MetaElementVO[] = MetaElementUtils.excludeInternalMetaElements(articleVO.metaElements);
      result = metaElements.map((connection: MetaElementVO) => {
        const connectionName: string = this.getMainConnectionName(connection.name);
        const item: CopyConnectionItem = new CopyConnectionItem(connectionName);
        if (this.isConnectionWithSameNameExists(sourceConnections, connectionName)) {
          item.sourceConnectionName = connectionName;
        } else {
          item.sourceConnectionName = '';
        }
        return item;
      });
    }
    return result;
  }

  private addNewConnectionItemsToTargetFromSource(): void {
    const sourceConnections: CopyConnectionItem[] = this.sourceConnectionList.filter((sourceConnection: CopyConnectionItem) => {
      return !this.isConnectionWithSameNameExists(this.targetConnectionList, sourceConnection.connectionName);
    });

    const connectionItems: CopyConnectionItem[] = sourceConnections.map((connection: CopyConnectionItem) => {
      const item: CopyConnectionItem = new CopyConnectionItem(connection.connectionName);
      item.sourceConnectionName = connection.connectionName;
      item.copyAttributes = true;
      item.copyProperties = true;
      item.isNewConnection = true;
      return item;
    });
    this.targetConnectionList.push(...connectionItems);
  }

  public getConnectionName(connectionItem: CopyConnectionItem): string {
    return connectionItem.isNewConnection ? connectionItem.connectionName + ' [NEW]' : connectionItem.connectionName;
  }

  public setSourceConnectionName(name: string, connectionItem: CopyConnectionItem): void {
    connectionItem.sourceConnectionName = name;

    if (!connectionItem.sourceConnectionName) {
      connectionItem.copyProperties = false;
      connectionItem.copyAttributes = false;
    } else {
      if (connectionItem.isNewConnection) {
        connectionItem.copyProperties = true;
        connectionItem.copyAttributes = true;
      }
    }
  }

  public sourcesSelectionHandler(checked: boolean): void {
    this.targetConnectionList.forEach((item: CopyConnectionItem) => {
      this.toggleSourceConnection(checked, item);
    });
    if (!checked) {
      this.isPropertiesChecked = false;
      this.isAttributesChecked = false;
    }
  }

  public toggleSourceConnection(checked: boolean, connectionItem: CopyConnectionItem): void {
    let name = '';
    if (checked) {
      const sourceListForConnection: CopyConnectionItem[] = this.getSourceListForConnection(connectionItem);
      if (this.isConnectionWithSameNameExists(sourceListForConnection, connectionItem.connectionName)) {
        name = connectionItem.connectionName;
      } else if (sourceListForConnection.length > 1) {
        name = sourceListForConnection[1].connectionName;
      }
    }

    this.setSourceConnectionName(name, connectionItem);
  }

  private isConnectionWithSameNameExists(connectionItems: CopyConnectionItem[], name: string): boolean {
    return connectionItems && connectionItems.some((item: CopyConnectionItem) => item && item.connectionName === name);
  }

  public selectionHandler(isMultiple: boolean, checked: boolean, isAttribute: boolean = false, connectionName: string = ''): void {
    this.targetConnectionList.forEach((item: CopyConnectionItem) => {
      const property: string = isAttribute ? 'copyAttributes' : 'copyProperties';
      if (isMultiple) {
        if (item.sourceConnectionName) {
          if (isAttribute && item.isNewConnection) {
            return;
          } else {
            item[property] = checked;
          }
        }
      } else {
        if (item.connectionName === connectionName) {
          item[property] = checked;
        }
      }
    });
  }

  public getSourceListForConnection(connectionItem: CopyConnectionItem): CopyConnectionItem[] {
    let result: CopyConnectionItem[];
    if (connectionItem.isNewConnection) {
      result = this.sourceConnectionList.filter((con: CopyConnectionItem) => {
        return connectionItem.connectionName === con.connectionName;
      });
    } else {
      result = this.sourceConnectionList.concat();
    }

    result.unshift(null);
    return result;
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  private async createSourceArticleContainer(): Promise<ArticleContainer> {
    const modelData: ArticleModelDataHolder = await this.modelLoadingService.loadModel(this.sourceArticleVO, false);
    const sourceContainer: ArticleContainer = this.articleContainerFactory.getNewInstance();
    const usage: ArticleUsageVO = this.projectArticleService.createUnstoredNewArticleUsage(this.sourceArticleVO.identity, this.sourceArticleVO.version);
    sourceContainer.updateArticle(this.sourceArticleVO);
    sourceContainer.updateArticleUsage(usage);
    await sourceContainer.parseModel(modelData);
    return sourceContainer;
  }

  private getTargetArticleContainer(): ArticleContainer {
    const targetArticleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
    return this.containerService.findArticleContainerInGlobal(targetArticleUsage.id);
  }

  private replaceTargetBySourceMetaData(targetConnectionName: string, sourceConnectionName: string): void {
    const metaElement: MetaElementVO = ConnectionInfoUtils.findMainMetaElementIgnoringPostfix(
      this.sourceArticleVO.metaElements,
      sourceConnectionName
    );
    const target: MetaElementVO = ConnectionInfoUtils.findMainMetaElementIgnoringPostfix(this.targetArticleVO.metaElements, targetConnectionName);
    ConnectionInfoUtils.replaceTargetBySourceMetaData(target, metaElement);
    this.connectionUtils.updateInternalConnectionPropertiesByName(target.name, this.targetArticleVO);
  }

  private copyAttributes(targetItem: CopyConnectionItem, sourceContainer: ArticleContainer): void {
    this.connectionUtils.replaceTargetBySourceConnection(
        this.connectionConverter,
      targetItem.sourceConnectionName,
      sourceContainer.connections,
      targetItem.connectionName,
      this.targetContainer,
      false
    );
    if (targetItem.isNewConnection) {
      this.clearConnectionProperties(targetItem);
    }
  }

  private clearConnectionProperties(targetItem: CopyConnectionItem): void {
    const metaElement: MetaElementVO = ConnectionInfoUtils.findMainMetaElementIgnoringPostfix(
      this.targetArticleVO.metaElements,
      targetItem.connectionName
    );
    metaElement.behaviorProperties = metaElement.behaviorProperties.filter((p) => PropertyUtils.isInternalConnectionPropertyName(p.name));
    if (metaElement instanceof ConnectionAreaVO) {
      metaElement.connectionPlausiProperties = [];
      metaElement.connectionRequirements = [];
      metaElement.connectionFeatures = [];
    }
    this.connectionUtils.updateInternalConnectionPropertiesByName(targetItem.connectionName, this.targetArticleVO);
  }

  public async save(): Promise<void> {
    this.selectionModelService.removeAllSelections();

    this.targetContainer = this.getTargetArticleContainer();
    const oldState: CopyFromOperationState = this.getStateForCopyFromOperation(this.targetContainer);

    const sourceContainer: ArticleContainer = this.copyConnections ? await this.createSourceArticleContainer() : null;
    if (sourceContainer) {
      this.targetConnectionList.forEach((item: CopyConnectionItem) => {
        if (item.copyAttributes) {
          this.copyAttributes(item, sourceContainer);
        }
        if (item.copyProperties) {
          this.replaceTargetBySourceMetaData(item.connectionName, item.sourceConnectionName);
        }
      });
      sourceContainer.disposeModel();
    }

    ArticleUtils.replaceTargetArticlePropertiesBySourceArticle(
      this.targetArticleVO,
      this.sourceArticleVO,
      this.copyConnectionProperties,
      this.copyBehaviorProperties,
      this.copyPlausiProperties
    );

    const newState: CopyFromOperationState = this.getStateForCopyFromOperation(this.targetContainer);
    const copyArticlePropertiesFromOperation: CopyArticlePropertiesFromOperation = this.admintoolOperationFactory.createCopyArticlePropertiesFromOperation(
        this.sourceArticleVO,
        this.getTargetArticleContainer(),
        this.targetConnectionList,
        {
          copyConnectionProperties: this.copyConnectionProperties,
          copyBehaviorProperties: this.copyBehaviorProperties,
          copyPlausiProperties: this.copyPlausiProperties,
        },
        oldState,
        newState
    );

    if (copyArticlePropertiesFromOperation != null) {
      await this.undoRedoHelper.pushUndoOperation(copyArticlePropertiesFromOperation);
    }
    this.sceneManager.renderScene();
    this.applicationRefreshService.notifyProjectChange();
    this.adminService.updateProperties();

    this.close();
  }

  private getMainConnectionName(connectionName: string): string {
    if (connectionName) {
      return MetaElementUtils.removeInternalConnectionPostfix(connectionName);
    }
    return connectionName;
  }

  private getStateForCopyFromOperation(articleContainer: ArticleContainer): CopyFromOperationState {
    let copiedArticle: ArticleVO;
    if (this.projectModel.isCompositeAdminView) {
      const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
      const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
      copiedArticle = CloneUtils.cloneArticleVO(article);
    } else {
      copiedArticle = CloneUtils.cloneArticleVO(articleContainer.article);
    }

    const connections: Connection[] = [];
    if (articleContainer) {
      articleContainer.connections.forEach((c: Connection) => {
        const mesh: Mesh = MeshUtils.cloneMesh(c.mesh);
        mesh.position.copy(new Vector3());
        mesh.rotation.copy(new Euler());
        const connection: Connection = this.connectionFactory.getNewInstance(articleContainer, mesh);
        connection.metaElementVO = copiedArticle.metaElements.find((meta: MetaElementVO) => meta.name === c.connectionName);
        connection.connectionName = c.connectionName;

        const sourceMeshWidthOrRadius: number = c.meshIsCircle ? c.meshRadius : c.meshWidth;
        const sourceMeshLength: number = c.meshIsCircle ? null : c.meshLength;

        connection.clearMeshValues();
        connection.updateMesh(c.meshPosition, c.meshRotationDegree, sourceMeshWidthOrRadius, sourceMeshLength);
        connections.push(connection);
      });
    }

    return new CopyFromOperationState(copiedArticle, connections);
  }
}
